import { ParamDeclarations, Params, ParamsIn } from './paramRegistry';
import { RouteNode } from './routeNode';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type EmptyParams = {};

/**
 * Base type for a location in the declarative router.
 *
 * A location is a description of a place that can be navigated to.
 *
 * NOTE: You likely want `AppLocation` instead, which is the specialized type for ardoq-front
 * with the exact routes and parameters used in the application.
 */
export type GenericLocation<
  RoutePath extends string = string,
  Params extends EmptyParams = Record<never, never>,
  SearchParams extends Partial<EmptyParams> = Record<never, never>,
  HiddenParams extends Partial<EmptyParams> = Record<never, never>,
> = {
  /**
   * The identifier for the route of this location.
   *
   * @example
   * ```ts
   * '/myRoute/:param/childRoute'
   * ```
   */
  route: RoutePath;

  /**
   * The parameters which specializes the route to the specific location.
   *
   * @example
   * For a route like `/myRoute/:param/childRoute`, the params could be:
   * ```ts
   * {
   *   param: 'myParamValue'
   * }
   * ```
   */
  params: Params;

  /**
   * Optional query parameters at this location.
   */
  searchParams?: SearchParams;

  /**
   * Optional hidden parameters at this location.
   *
   * Stored in browser history state, not visible in the URL.
   * Survives back/forward navigation but lost on page reload or URL sharing.
   */
  hiddenParams?: HiddenParams;

  /**
   * Optional URL hash fragment, including the leading `#` (e.g. `#section-id`).
   *
   * Not part of route matching; carried through so the router can scroll to the
   * matching element after navigation.
   */
  hash?: string;
};

/**
 * An expansion of {@link GenericLocation} that includes the result of matching a URL to a route tree.
 *
 * This should only be used by the internals of the declarative router.
 * @internal
 */
export type MatchedLocation<
  RoutePath extends string = string,
  Params extends EmptyParams = Record<never, never>,
  SearchParams extends Partial<EmptyParams> = Record<never, never>,
  HiddenParams extends Partial<EmptyParams> = Record<never, never>,
> = GenericLocation<RoutePath, Params, SearchParams, HiddenParams> & {
  kind: 'matched';
  /**
   * @internal
   */
  activeRoutes: RouteNode[];
  /**
   * @internal
   */
  unmatchedSuffix: string | null;

  /**
   * Query parameters matched at this location.
   *
   * If no query params are provided, this will be an empty object.
   */
  searchParams: SearchParams;

  /**
   * Hidden parameters at this location.
   *
   * If no hidden params are provided, this will be an empty object.
   */
  hiddenParams: HiddenParams;

  /**
   * Flagg added by the {@link declarativeRouter} when doing HMR, in order to bust
   * the component cache in {@link connectRouter}.
   */
  isHmrRender?: boolean;

  /**
   * History action for this navigation.
   * @internal
   */
  action: 'push' | 'replace';
};

export type UnmatchedLocation = GenericLocation<
  '',
  EmptyParams,
  Partial<EmptyParams>,
  Partial<EmptyParams>
> & {
  kind: 'unmatched';
  activeRoutes: [];
  unmatchedSuffix: string;
  hiddenParams: Partial<EmptyParams>;
  isHmrRender?: boolean;
  action: 'push' | 'replace';
};

export const unmatchedLocation: UnmatchedLocation = {
  kind: 'unmatched',
  route: '',
  params: {},
  searchParams: {},
  hiddenParams: {},
  activeRoutes: [],
  unmatchedSuffix: '',
  action: 'push',
};

/**
 * A location whose URL could not be fully matched against the route tree.
 *
 * `activeRoutes` holds the prefix that _did_ match, so the not found content can
 * be resolved against it while the layouts above stay mounted. `matchedRoute`
 * identifies that prefix. Both are empty when nothing below the root matched.
 * `params` holds the params of that prefix, so content rendered around the
 * not-found page, such as permission boundaries, can read the resource it is
 * guarding. `searchParams` and `hiddenParams` are the ones the request carried.
 *
 * `route` is pinned to `''` so that {@link filterRouteActive} keeps the streams
 * of the routes below out of it. Streams that read a param without checking the
 * route do see it.
 *
 * Distinct from {@link UnmatchedLocation}, which is the "no location" sentinel
 * used before the router has arrived anywhere and when leaving the declarative
 * router for a legacy module. Both pin `route` to `''`, so `kind` is what tells
 * them apart.
 */
export type NotFoundLocation = Omit<
  MatchedLocation<
    '',
    Partial<Params>,
    Readonly<Record<string, unknown>>,
    Partial<Params>
  >,
  'kind'
> & {
  kind: 'not-found';
  matchedRoute: string;
  unmatchedSuffix: string;

  /**
   * The path that was asked for.
   *
   * The router writes it to the address bar for a navigation that came from the
   * app rather than the browser.
   */
  requestedPath: string;
};

export const isNotFoundLocation = (
  location: GenericLocation | NotFoundLocation
): location is NotFoundLocation =>
  'kind' in location && location.kind === 'not-found';

export type LocationAndTitle = {
  location: MatchedLocation | UnmatchedLocation | NotFoundLocation;
  title?: string;
  // When title updates dynamically, we want to replace the history entry instead of pushing a new one
  // The first emit will always have the action from the location, but subsequent emits for dynamic titles will have 'replace'
  action: 'push' | 'replace';
};

export const unmatchedLocationAndTitle: LocationAndTitle = {
  location: unmatchedLocation,
  title: undefined,
  action: unmatchedLocation.action,
};

export type PartialLocation = MatchedLocation<
  string,
  Partial<Params>,
  Partial<Params>,
  Partial<Params>
>;

/**
 * Like intersection with `&`, but makes typescript infer a single object instead
 * of an intersection. So you will get `{ a: string, b: number }` instead of
 * `{ a: string } & { b: number }`.
 */
export type FlatIntersection<S, T> = S & T extends infer U
  ? { [K in keyof U]: U[K] }
  : never;

type ExtractParam<PathSegment extends string> =
  PathSegment extends `/:${infer Param}`
    ? Param extends keyof Params
      ? Pick<Params, Param>
      : EmptyParams
    : EmptyParams;

/**
 * Helper type to collect all keys from a readonly array into a union.
 */
type CollectKeys<T extends readonly any[]> = T extends readonly (infer K)[]
  ? K
  : never;

/**
 * Extract search parameters from a route node.
 * Makes all search parameters optional.
 */
type ExtractSearchParams<Node> = Node extends {
  searchParams: infer Declared;
}
  ? Declared extends ParamDeclarations
    ? Partial<ParamsIn<Declared>>
    : Partial<EmptyParams>
  : Partial<EmptyParams>;

/**
 * Extract hidden parameters from a route node.
 * Makes all hidden parameters optional.
 */
type ExtractHiddenParams<Node> = Node extends {
  hiddenParams: readonly any[];
}
  ? CollectKeys<Node['hiddenParams']> extends keyof Params
    ? Partial<Pick<Params, CollectKeys<Node['hiddenParams']>>>
    : Partial<EmptyParams>
  : Partial<EmptyParams>;

export type NextDepth = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export type MaxRecursionDepth = 10;

/**
 * A place that can be navigated to in the given route tree.
 */
export type LocationIn<
  RouteTree extends RouteNode,
  ParentPath extends string = '',
  AccumulatedParams extends EmptyParams = EmptyParams,
  AccumulatedSearchParams extends Partial<EmptyParams> = Partial<EmptyParams>,
  AccumulatedHiddenParams extends Partial<EmptyParams> = Partial<EmptyParams>,
  Depth extends number = 0,
> = Depth extends MaxRecursionDepth
  ? never
  : RouteTree extends {
        path: infer PathSegment extends string;
        children: infer Children extends readonly any[];
      }
    ?
        | GenericLocation<
            `${ParentPath}${PathSegment}`,
            FlatIntersection<ExtractParam<PathSegment>, AccumulatedParams>,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >
          >
        | LocationIn<
            Children[number],
            `${ParentPath}${PathSegment}`,
            ExtractParam<PathSegment> & AccumulatedParams,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >,
            NextDepth[Depth]
          >
    : RouteTree extends {
          children: infer Children extends readonly any[];
        }
      ? LocationIn<
          Children[number],
          ParentPath,
          AccumulatedParams,
          FlatIntersection<
            ExtractSearchParams<RouteTree>,
            AccumulatedSearchParams
          >,
          FlatIntersection<
            ExtractHiddenParams<RouteTree>,
            AccumulatedHiddenParams
          >,
          NextDepth[Depth]
        >
      : RouteTree extends {
            path: infer PathSegment extends string;
          }
        ? GenericLocation<
            `${ParentPath}${PathSegment}`,
            FlatIntersection<ExtractParam<PathSegment>, AccumulatedParams>,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >
          >
        : never;

/**
 * The result of matching a URL to the given route tree.
 *
 * This is for internal use in the declarative router. {@link LocationIn} is a subtype of this, for pulic use.
 * @internal
 */
export type MatchedLocationIn<
  RouteTree extends RouteNode,
  ParentPath extends string = '',
  AccumulatedParams extends EmptyParams = EmptyParams,
  AccumulatedSearchParams extends Partial<EmptyParams> = Partial<EmptyParams>,
  AccumulatedHiddenParams extends Partial<EmptyParams> = Partial<EmptyParams>,
  Depth extends number = 0,
> = Depth extends MaxRecursionDepth
  ? never
  : RouteTree extends {
        path: infer PathSegment extends string;
        children: infer Children extends readonly any[];
      }
    ?
        | MatchedLocation<
            `${ParentPath}${PathSegment}`,
            FlatIntersection<ExtractParam<PathSegment>, AccumulatedParams>,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >
          >
        | MatchedLocationIn<
            Children[number],
            `${ParentPath}${PathSegment}`,
            ExtractParam<PathSegment> & AccumulatedParams,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >,
            NextDepth[Depth]
          >
    : RouteTree extends {
          children: infer Children extends readonly any[];
        }
      ? MatchedLocationIn<
          Children[number],
          ParentPath,
          AccumulatedParams,
          FlatIntersection<
            ExtractSearchParams<RouteTree>,
            AccumulatedSearchParams
          >,
          FlatIntersection<
            ExtractHiddenParams<RouteTree>,
            AccumulatedHiddenParams
          >,
          NextDepth[Depth]
        >
      : RouteTree extends {
            path: infer PathSegment extends string;
          }
        ? MatchedLocation<
            `${ParentPath}${PathSegment}`,
            FlatIntersection<ExtractParam<PathSegment>, AccumulatedParams>,
            FlatIntersection<
              ExtractSearchParams<RouteTree>,
              AccumulatedSearchParams
            >,
            FlatIntersection<
              ExtractHiddenParams<RouteTree>,
              AccumulatedHiddenParams
            >
          >
        : never;

/**
 * A location the router has arrived at: either a full match in the route tree,
 * or a path that only matched a prefix of it.
 */
export type ArrivedLocation<RouteTree extends RouteNode> =
  | MatchedLocationIn<RouteTree>
  | NotFoundLocation;

export const isUnmatchedLocation = (
  location: UnmatchedLocation | PartialLocation | NotFoundLocation
): location is UnmatchedLocation => location.kind === 'unmatched';

/**
 * Extract all possible keys from a union of object types.
 * For union types like `{} | { sorting: SortDescriptor }`, this returns `'sorting'`.
 */
export type UnionKeys<T> = T extends any ? keyof T : never;

/**
 * Union of all route paths/identifiers in the given route tree.
 */
export type RouteIn<
  RouteTree extends RouteNode,
  AccumulatedPath extends string = '',
  Depth extends number = 0,
> = Depth extends MaxRecursionDepth
  ? never
  : RouteTree extends {
        path: infer PathSegment extends string;
      }
    ?
        | `${AccumulatedPath}${PathSegment}`
        | (RouteTree extends {
            children: infer Children extends readonly any[];
          }
            ? RouteIn<
                Children[number],
                `${AccumulatedPath}${PathSegment}`,
                NextDepth[Depth]
              >
            : never)
    : RouteTree extends {
          children: infer Children extends readonly any[];
        }
      ? RouteIn<Children[number], AccumulatedPath, NextDepth[Depth]>
      : never;

/**
 * Params of the specified route.
 *
 * Uses `Params` from the param registry to get the types of the parameters.
 */
export type ParamsAt<Route extends string> =
  Route extends `/${infer Segment}/${infer Rest}`
    ? FlatIntersection<ExtractParam<`/${Segment}`>, ParamsAt<`/${Rest}`>>
    : ExtractParam<Route>;

/**
 * The search parameters of the specified route, as declared in the route tree.
 */
export type SearchParamsAt<RouteTree extends RouteNode, Route extends string> =
  LocationIn<RouteTree> extends infer Location
    ? Location extends {
        route: Route;
        searchParams?: infer SearchParams;
      }
      ? SearchParams
      : never
    : never;

export type PopPath<
  Path extends string,
  AccumulatedPath extends string = '',
  Depth extends number = 0,
> = Depth extends MaxRecursionDepth
  ? never
  : Path extends `/${infer Segment}/${infer Rest}`
    ? PopPath<`/${Rest}`, `${AccumulatedPath}/${Segment}`, NextDepth[Depth]>
    : // This is the final segment, so we return the accumulated path
      AccumulatedPath;

/**
 * Extracts the paths with the given parameter name from the given union of paths.
 */
export type PathWithParam<
  Path extends string,
  ParamName extends string,
  AccumulatedPath extends string = '',
  Depth extends number = 0,
> = Depth extends MaxRecursionDepth
  ? never
  : Path extends `/${infer Segment}/${infer Rest}`
    ? Segment extends `:${ParamName}`
      ? // At a segment with the necessary param, so we can include the path
        `${AccumulatedPath}/${Segment}/${Rest}`
      : // At a segment without the necessary param, so we need to continue looking
        PathWithParam<
          `/${Rest}`,
          ParamName,
          `${AccumulatedPath}/${Segment}`,
          NextDepth[Depth]
        >
    : // This is the final segment, so we return the accumulated path
      Path extends `/:${ParamName}`
      ? `${AccumulatedPath}${Path}`
      : never;
